import { useState, useCallback, useMemo } from 'react';

/**
 * Custom hook to manage search functionality
 * @param {Array} items - Array of items to search through
 * @param {Array} searchFields - Array of field names to search in (e.g., ['title', 'description'])
 * @returns {Object} - search state and methods
 */
const useSearch = (items = [], searchFields = ['title']) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    
    if (!searchTerm) return items;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    return items.filter(item => {
      return searchFields.some(field => {
        const fieldValue = item[field];
        return fieldValue && 
          typeof fieldValue === 'string' && 
          fieldValue.toLowerCase().includes(lowerSearchTerm);
      });
    });
  }, [items, searchTerm, searchFields]);

  return {
    searchTerm,
    setSearchTerm,
    handleSearchChange,
    clearSearch,
    filteredItems,
    hasResults: filteredItems.length > 0,
    isSearching: searchTerm.length > 0
  };
};

export default useSearch;
